import React, { useEffect, useState } from 'react';
import EntryLayoutScreen from '../../shared/EntryLayout/EntryLayout';

import styles from './ResetPasswordForm.module.css';

import { Typography } from '@mui/material';
import { useInput } from '../../../customHook/useInput';
import { validatePassword } from '../../../helper/validate';
import { AuthService } from '../../../service/auth.service';
import zeplinColor from '../../../theme/Color';
import { Constants, Error } from '../../../utilities/constants';
import CustomButton from '../../shared/CustomButton/CustomButton';
import CustomPassword from '../../shared/CustomPassword/CustomPassword';

type ResetPasswordFormProps = {
  user: string;
  otp: string;
  setNext: React.Dispatch<React.SetStateAction<string>>;
};

const ResetPasswordForm = ({ user, otp, setNext }: ResetPasswordFormProps) => {
  const [mismatch, setMismatch] = useState(false);
  const [alert, setAlert] = useState('');
  const [error, setError] = useState(false);
  const {
    handleChange: handlePasswordChange,
    handleBlur: handlePasswordBlur,
    value: password,
    invalid: invalidPassword,
    didChange: didPasswordChange,
  } = useInput('', (text) => validatePassword(text).valid);
  const {
    handleChange: handleConfirmPasswordChange,
    handleBlur: handleConfirmPasswordBlur,
    value: confirmPassword,
  } = useInput('', (text) => validatePassword(text).valid);

  const [check, setCheck] = useState<{
    valid: boolean;
    hasLowercase: boolean;
    hasUppercase: boolean;
    hasMinLength: boolean;
    hasSymbol: boolean;
    hasNumber: boolean;
  }>({
    valid: false,
    hasLowercase: false,
    hasUppercase: false,
    hasMinLength: false,
    hasSymbol: false,
    hasNumber: false,
  });
  useEffect(() => {
    setCheck(validatePassword(password));
  }, [password]);

  const valid = confirmPassword?.length && password?.length;

  const resetPassword = async () => {
    try {
      if (confirmPassword === password) {
        const res = await AuthService.setPassword({
          userName: user,
          confirmationCode: Number(otp),
          password: password,
        });
        console.log(res);
        if (res?.$metadata?.httpStatusCode === 200) {
          sessionStorage.setItem('next', 'confirm');
          setNext('confirm');
        }
      } else {
        setMismatch(true);
      }
    } catch (err: any) {
      setError(true);
      const message: Error = err?.response?.data?.name || '';
      setAlert(Constants.ErrorText[message]);
    }
  };
  const children = (
    <div className={styles['reset-field-input']}>
      <CustomPassword
        style={{ width: '50%', margin: 'auto' }}
        onBlur={handlePasswordBlur}
        onChange={handlePasswordChange}
        name='password'
        text={password}
        error={invalidPassword && didPasswordChange ? 'Create valid password' : ''}
        label='New password'
      />

      <div className={styles['password-rule']} style={{ color: zeplinColor.Neutral9 }}>
        <div className={styles['validation-points']}>
          <img
            src={check?.hasMinLength ? '/assets/check.svg' : '/assets/dot.svg'}
            width={20}
            height={20}
          />
          <Typography variant='body3' style={{ color: zeplinColor.Neutral9 }}>
            At least 8 characters
          </Typography>
        </div>
        <div className={styles['validation-points']}>
          <img
            src={check?.hasNumber ? '/assets/check.svg' : '/assets/dot.svg'}
            width={20}
            height={20}
          />
          <Typography variant='body3' style={{ color: zeplinColor.Neutral9 }}>
            At least 1 number
          </Typography>
        </div>
        <div className={styles['validation-points']}>
          <img
            src={
              check?.hasLowercase && check?.hasUppercase ? '/assets/check.svg' : '/assets/dot.svg'
            }
            width={20}
            height={20}
          />
          <Typography variant='body3' style={{ color: zeplinColor.Neutral9 }}>
            At least 1 uppercase & 1 lowercase letter
          </Typography>
        </div>
        <div className={styles['validation-points']}>
          <img
            src={check?.hasSymbol ? '/assets/check.svg' : '/assets/dot.svg'}
            width={20}
            height={20}
          />
          <Typography variant='body3' style={{ color: zeplinColor.Neutral9 }}>
            At least 1 special character
          </Typography>
        </div>
      </div>
      <CustomPassword
        style={{ width: '50%', margin: 'auto' }}
        onBlur={handleConfirmPasswordBlur}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          handleConfirmPasswordChange(e);
          setMismatch(false);
        }}
        name='confirmPassword'
        text={confirmPassword}
        error={mismatch ? 'Password must match' : ''}
        label='Confirm new password'
      />
      <CustomButton
        onClick={resetPassword}
        variant='contained'
        disabled={valid ? false : true}
        color={valid ? 'primary' : 'customDisable'}
        style={{ width: '50%', borderRadius: '14px', height: 56 }}
        textStyle={{ color: valid ? zeplinColor.Neutral1 : zeplinColor.Neutral8 }}
        text={'Reset Password'}
      />
    </div>
  );

  return (
    <EntryLayoutScreen
      header={'Create a new password'}
      subheader={`Almost done. Enter your new password and you're good to go.`}
      input={children}
      alternative={''}
      footerText=''
      footerButtonText=''
      icon={
        <div
          style={{
            backgroundColor: zeplinColor.Neutral3,
            borderRadius: '70px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            margin: 'auto',
            width: '80px',
            height: '80px',
            padding: '20px',
          }}
        >
          <img src={'/assets/reset-password.svg'} width={40} height={40} />
        </div>
      }
      clickAction={() => {}}
      alert={alert}
      error={error}
    />
  );
};

export default ResetPasswordForm;
